/**
 * Boost renders both its instant-search dropdown and search-results page
 * after the theme has loaded. Remove price elements as they are added so
 * they do not remain in the rendered DOM.
 */
(() => {
  // Change this number to control cards shown in the split two-column layout.
  const maxInstantSearchProducts = 4;
  const isSearchPage = document.body?.classList.contains('search-template')
    || window.location.pathname === '/search';
  let userSelectedSearchTab = false;
  let isAutoSwitchingSearchTab = false;
  const resultRootSelector = [
    '#boost-sd__search-widget-init-wrapper',
    '.boost-sd__instant-search-results--mobile',
    '.boost-sd__search-opening',
    ...(isSearchPage
      ? [
        '.boost-sd-container-search-page',
        '.boost-sd__filter-product-list',
        '.boost-sd__product-filter-fallback',
      ]
      : []),
  ].join(',');
  const placeholderImage = window.boostInstantSearchTheme?.placeholderImage;
  const priceSelector = [
    '.boost-sd__suggestion-queries-item-price',
    '.boost-sd__instant-search-product-price',
    '.boost-sd__product-price-wrapper',
    '.boost-sd__product-price',
    '.boost-sd__product-item-list-view-layout-price',
    '.boost-sd__quick-view-price',
    '[class*="price"]',
  ].join(',');
  const vendorSelector = [
    '.boost-sd__suggestion-queries-item-vendor',
    '.boost-sd__instant-search-product-vendor',
    '.boost-sd__product-vendor',
    '[class*="vendor"]',
  ].join(',');
  const soldOutBadgeSelector = [
    '.boost-sd__filter-product-list .boost-sd__product-label--soldout',
    '.boost-sd__product-filter-fallback .boost-sd__product-label--soldout',
  ].join(',');

  const removePrices = (element) => {
    if (!element || !('querySelectorAll' in element)) return;

    if (element instanceof Element && element.matches(priceSelector)) {
      element.remove();
      return;
    }

    element.querySelectorAll(priceSelector).forEach((price) => price.remove());
  };

  const removeVendors = (element) => {
    if (!element || !('querySelectorAll' in element)) return;

    if (element instanceof Element && element.matches(vendorSelector)) {
      element.remove();
      return;
    }

    element.querySelectorAll(vendorSelector).forEach((vendor) => vendor.remove());
  };

  const removeSoldOutBadges = (element) => {
    if (!isSearchPage || !element || !('querySelectorAll' in element)) return;

    if (element instanceof Element && element.matches(soldOutBadgeSelector)) {
      element.remove();
      return;
    }

    element.querySelectorAll(soldOutBadgeSelector).forEach((badge) => badge.remove());
  };

  const limitProducts = (element) => {
    if (!element || !('querySelectorAll' in element)) return;

    const isFullWidthProducts = Boolean(element.querySelector(
      '.boost-sd__instant-search-autocomplete.boost-sd__instant-search-no-other:not(.boost-sd__instant-search-no-product)',
    ));
    const productLimit = isFullWidthProducts ? 6 : maxInstantSearchProducts;

    element
      .querySelectorAll('.boost-sd__instant-search-product-list-items')
      .forEach((list) => {
        Array.from(list.children)
          .forEach((product, index) => product.classList.toggle(
            'boost-search-product-limit-hidden',
            index >= productLimit,
          ));
      });
  };

  const replacePlaceholderImages = (element) => {
    if (!placeholderImage || !element || !('querySelectorAll' in element)) return;

    element
      .querySelectorAll('img')
      .forEach((image) => {
        const source = image.getAttribute('src')
          || image.getAttribute('data-src')
          || image.getAttribute('srcset')
          || '';
        const isAppPlaceholder = /(?:placeholder|no[-_ ]?image|image[-_ ]?not[-_ ]?found|default[-_ ]?(?:product|image)|blank)/i.test(source)
          || /(?:placeholder|no[-_ ]?image)/i.test(image.alt || '')
          || Boolean(image.closest('[class*="placeholder"], [class*="no-image"]'))
          || source.startsWith('data:image/svg+xml');
        const isThemePlaceholder = /placeholder-image\.svg/i.test(source);

        if ((!source || isAppPlaceholder) && !isThemePlaceholder) {
          image.removeAttribute('srcset');
          image.removeAttribute('data-src');
          image.removeAttribute('data-srcset');
          image.src = placeholderImage;
        }

        if (!image.dataset.boostPlaceholderFallback) {
          image.dataset.boostPlaceholderFallback = 'true';
          image.addEventListener('error', () => {
            if (image.src !== placeholderImage) image.src = placeholderImage;
          }, { once: true });
        }
      });
  };

  const addProductResultCount = (element) => {
    if (!element || !('querySelector' in element)) return;

    const productsColumn = element.querySelector('.boost-sd__instant-search-column--products');
    if (!productsColumn) return;

    const viewAllText = productsColumn.querySelector('.boost-sd__view-all')?.textContent || '';
    const resultCount = viewAllText.match(/\d[\d,.]*/)?.[0]
      || productsColumn.querySelectorAll('.boost-sd__instant-search-product-list-items > *').length;

    const heading = Array.from(productsColumn.querySelectorAll('.boost-sd__header-item'))
      .find((item) => item.textContent.trim().toLowerCase().startsWith('products'));

    if (!heading || !resultCount) return;

    let count = heading.querySelector('.boost-search-results-count');
    if (!count) {
      count = document.createElement('span');
      count.className = 'boost-search-results-count';
      heading.append(count);
    }

    const countText = ` ${resultCount} results`;
    if (count.textContent !== countText) count.textContent = countText;
  };

  const markPagesGroup = (element) => {
    if (!element || !('querySelectorAll' in element)) return;

    element
      .querySelectorAll('.boost-sd__instant-search-column--others .boost-sd__suggestion-queries-group')
      .forEach((group) => {
        const heading = group.querySelector('.boost-sd__header-item');
        const isPagesGroup = heading?.textContent.trim().toLowerCase().startsWith('pages');
        group.classList.toggle('boost-search-pages-group', Boolean(isPagesGroup));
      });
  };

  const getInstantSearchInput = () => document.querySelector('.custom-header__search-input');

  const updateRecentSearchesLayout = (element) => {
    if (!element || !('querySelector' in element)) return;

    const searchInput = getInstantSearchInput();
    const autocomplete = element.querySelector('.boost-sd__instant-search-autocomplete');
    const isRecentSearches = Boolean(autocomplete && searchInput && !searchInput.value.trim());

    element.classList.toggle('boost-search-recent-searches', isRecentSearches);
    if (!isRecentSearches) return;

    const inputRect = searchInput.getBoundingClientRect();
    const widgetRect = element.getBoundingClientRect();
    element.style.setProperty('--boost-recent-search-left', `${inputRect.left - widgetRect.left}px`);
    element.style.setProperty('--boost-recent-search-width', `${inputRect.width}px`);
  };

  const showNoProductMessageForSearchTerm = (element) => {
    if (!element || !('querySelector' in element)) return;

    const autocomplete = element.querySelector('.boost-sd__instant-search-autocomplete');
    const searchTerm = getInstantSearchInput()?.value.trim() || '';
    const productItemCount = element.querySelectorAll([
      '.boost-sd__instant-search-product-list-items > *',
      '.boost-sd__suggestion-queries-item--product',
    ].join(',')).length;
    const hasNoProducts = autocomplete?.classList.contains('boost-sd__instant-search-no-product')
      || Boolean(autocomplete?.querySelector('.boost-sd__no-search-result'))
      || productItemCount === 0;
    let productsColumn = element.querySelector('.boost-sd__instant-search-column--products');
    const isSettledSearch = element.dataset.boostSearchSettledTerm === searchTerm;
    const shouldShowEmptyState = Boolean(searchTerm && autocomplete && isSettledSearch && hasNoProducts);
    const existingMessage = element.querySelector('.boost-search-no-products-message');

    element.classList.toggle('boost-search-no-products-for-term', shouldShowEmptyState);

    if (!shouldShowEmptyState) {
      existingMessage?.remove();
      return;
    }

    if (!productsColumn) {
      productsColumn = document.createElement('div');
      productsColumn.className = 'boost-sd__instant-search-column boost-sd__instant-search-column--products';
      autocomplete.append(productsColumn);
    }

    const message = existingMessage || document.createElement('div');
    if (!existingMessage) {
      message.className = 'boost-search-no-products-message';
      productsColumn.append(message);
    }

    let title = message.querySelector('.boost-search-no-products-message__title');
    let description = message.querySelector('.boost-search-no-products-message__description');

    if (!title || !description) {
      message.replaceChildren();
      title = document.createElement('strong');
      title.className = 'boost-search-no-products-message__title';
      description = document.createElement('span');
      description.className = 'boost-search-no-products-message__description';
      message.append(title, description);
    }

    const titleText = `No matching products for "${searchTerm}"`;
    if (title.textContent !== titleText) title.textContent = titleText;
    description.textContent = 'Try a different spelling or search term.';
  };

  const getSearchTabResultCount = (tab) => {
    const countSource = `${tab.getAttribute('aria-label') || ''} ${tab.textContent || ''}`;
    const countMatch = countSource.match(/(?:showing\s+|\()([\d,.]+)(?:\s+results|\))/i);

    if (!countMatch) return null;
    return Number.parseInt(countMatch[1].replace(/[^\d]/g, ''), 10);
  };

  const showFirstSearchTabWithResults = (element) => {
    if (
      !isSearchPage
      || userSelectedSearchTab
      || isAutoSwitchingSearchTab
      || !element
      || !('querySelector' in element)
    ) return;

    const searchPage = element.matches?.('.boost-sd-container-search-page')
      ? element
      : element.closest?.('.boost-sd-container-search-page')
        || element.querySelector('.boost-sd-container-search-page');
    const tabList = searchPage?.querySelector('[role="tablist"][aria-label="Search Result Tabs Toolbar"]');
    if (!tabList) return;

    const tabs = Array.from(tabList.querySelectorAll('[role="tab"]'));
    const activeTab = tabs.find((tab) => tab.getAttribute('aria-selected') === 'true'
      || tab.classList.contains('boost-sd__search-result-toolbar-panel--active'));

    if (!activeTab || getSearchTabResultCount(activeTab) !== 0) return;

    const firstTabWithResults = tabs.find((tab) => tab !== activeTab
      && getSearchTabResultCount(tab) > 0
      && tab.dataset.searchPageTabBound === 'true');
    if (!firstTabWithResults) return;

    isAutoSwitchingSearchTab = true;
    firstTabWithResults.click();
    window.requestAnimationFrame(() => {
      isAutoSwitchingSearchTab = false;
    });
  };

  const cleanResults = (element) => {
    removePrices(element);
    removeVendors(element);
    removeSoldOutBadges(element);
    limitProducts(element);
    replacePlaceholderImages(element);
    addProductResultCount(element);
    markPagesGroup(element);
    updateRecentSearchesLayout(element);
    showNoProductMessageForSearchTerm(element);
    showFirstSearchTabWithResults(element);
  };

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      const changedNodes = mutation.type === 'attributes'
        ? [mutation.target]
        : mutation.addedNodes;

      changedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;

        const widget = node.matches(resultRootSelector)
          ? node
          : node.closest(resultRootSelector);

        if (widget) cleanResults(widget);
      });
    });
  });

  const start = () => {
    document.querySelectorAll(resultRootSelector).forEach(cleanResults);

    document.addEventListener('click', (event) => {
      const searchTab = event.target instanceof Element
        ? event.target.closest('.boost-sd-container-search-page [role="tab"]')
        : null;

      if (searchTab && !isAutoSwitchingSearchTab) userSelectedSearchTab = true;
    }, true);

    document.addEventListener('submit', (event) => {
      if (event.target instanceof Element && event.target.matches('.boost-sd__search-form')) {
        userSelectedSearchTab = false;
      }
    }, true);

    let inputRefreshTimer;
    document.addEventListener('input', (event) => {
      if (!(event.target instanceof Element) || !event.target.matches('.custom-header__search-input')) return;

      clearTimeout(inputRefreshTimer);
      const searchTerm = event.target.value.trim();

      if (!searchTerm) {
        document.querySelectorAll(resultRootSelector).forEach((root) => {
          delete root.dataset.boostSearchSettledTerm;
          cleanResults(root);
        });
        return;
      }

      inputRefreshTimer = window.setTimeout(() => {
        document.querySelectorAll(resultRootSelector).forEach((root) => {
          root.dataset.boostSearchSettledTerm = searchTerm;
          cleanResults(root);
        });
      }, 450);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-src', 'src', 'srcset'],
      childList: true,
      subtree: true,
    });

    // The mobile header uses a link as its search trigger. Open the existing
    // drawer directly so a missing component listener cannot fall back to the
    // empty /search page.
    document.addEventListener('click', (event) => {
      if (window.matchMedia('(min-width: 990px)').matches) return;

      const trigger = event.target instanceof Element
        ? event.target.closest('.custom-header__mobile-bar .search-drawer-button, .mobile-sticky-bar .search-drawer-button')
        : null;
      const drawer = document.getElementById('SearchDrawer');

      if (!trigger || !drawer) return;
      event.preventDefault();

      if (typeof drawer.show === 'function') drawer.show(trigger);
      else {
        drawer.hidden = false;
        drawer.setAttribute('open', '');
      }

      window.requestAnimationFrame(() => drawer.querySelector('input[type="search"]')?.focus());
    });
  };

  if (document.documentElement) start();
  else document.addEventListener('DOMContentLoaded', start, { once: true });
})();
